import {
  DevicesOrderBy,
  type DevicesDataExtraction_GetDevicesQuery,
} from '@/__generated__/graphql';
import { useQuery } from '@apollo/client';
import { ALLOWED_OPERATIONAL_STATES } from '@features/reports/utils/allowedOperationalStates';
import StandardAutocompleteIds from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Search } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { FixedSizeList } from 'react-window-1.8.10';
import Loading from 'src/components/Loading';
import GET_DATA_EXTRACTION_DEVICES from '../../graphql/queries/getDataExtractionDevices';

interface Props {
  value: string[];
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedDevices: string[]) => void;
}

function normalizeString(str: string) {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

const DeviceSelectDialog: FC<Props> = ({ value, onClose, open, onConfirm }) => {
  const { _ } = useLingui();
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<number[]>([]);
  const { data, loading } = useQuery(GET_DATA_EXTRACTION_DEVICES, {
    variables: {
      input: {
        orderBy: DevicesOrderBy.NameAsc,
        operationalStates: ALLOWED_OPERATIONAL_STATES,
        collectionIds: selectedCollectionIds.length > 0 ? selectedCollectionIds : undefined,
      },
    },
  });
  const [searchText, setSearchText] = useState<string>('');
  const [selectedDevices, setSelectedDevices] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setSelectedDevices(new Set(value));
      setSearchText('');
      setSelectedCollectionIds([]);
    }
  }, [open]);

  const filteredDevices = useMemo(() => {
    return (data?.devices ?? []).filter((device) => {
      if (
        searchText.length > 0 &&
        !normalizeString(device.profile.name).includes(normalizeString(searchText)) &&
        !normalizeString(device.id).includes(normalizeString(searchText))
      ) {
        return false;
      }
      return true;
    });
  }, [data, searchText]);

  const handleCheck = (deviceId: string) => () => {
    if (selectedDevices.has(deviceId)) {
      selectedDevices.delete(deviceId);
      setSelectedDevices(new Set(selectedDevices));
    } else {
      setSelectedDevices(new Set(selectedDevices.add(deviceId)));
    }
  };

  const handleCheckAll = () => {
    filteredDevices.map((i) => selectedDevices.add(i.id));
    setSelectedDevices(new Set(selectedDevices));
  };
  const handleUncheckAll = () => {
    filteredDevices.map((i) => selectedDevices.delete(i.id));
    setSelectedDevices(new Set(selectedDevices));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onConfirm(data?.devices.filter((i) => selectedDevices.has(i.id)).map((d) => d.id) ?? []);
          onClose();
        }}
      >
        <DialogTitle>
          <Trans>Seleccione los dispositivos para el reporte</Trans>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Loading />
          ) : (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', mt: 1 }}>
                <StandardAutocompleteIds
                  size="small"
                  multiple
                  dataType="icnCollections"
                  value={selectedCollectionIds}
                  onChange={(_, newValue) => setSelectedCollectionIds(newValue)}
                />
                <TextField
                  size="small"
                  placeholder={_(msg`Buscar dispositivo`)}
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ opacity: 0.5 }} fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', my: 2, justifyContent: 'space-between' }}>
                  <Box sx={{ gap: 2, display: 'flex' }}>
                    <Button
                      color="info"
                      variant="contained"
                      onClick={handleCheckAll}
                      disabled={filteredDevices.every((device) => selectedDevices.has(device.id))}
                    >
                      <Trans>Seleccionar todos</Trans>
                    </Button>
                    <Button
                      color="info"
                      variant="outlined"
                      onClick={handleUncheckAll}
                      disabled={!filteredDevices.some((device) => selectedDevices.has(device.id))}
                    >
                      <Trans>Deseleccionar todos</Trans>
                    </Button>
                  </Box>

                  <Box sx={{ alignSelf: 'center', justifyContent: 'flex-end' }}>
                    <DeviceCounter
                      selectedDevices={[...selectedDevices].map((id) => {
                        return {
                          id: id,
                          name:
                            data?.devices.find((device) => device.id === id)?.profile.name ?? '',
                        };
                      })}
                    />
                  </Box>
                </Box>
                <Divider />
                <FixedSizeList
                  height={400}
                  itemCount={filteredDevices.length}
                  itemSize={35}
                  width="100%"
                >
                  {({ index, style }) => {
                    const device = filteredDevices[index];
                    return (
                      <ListItem
                        key={device.id}
                        style={style}
                        secondaryAction={
                          <Checkbox
                            edge="end"
                            onChange={handleCheck(device.id)}
                            checked={selectedDevices.has(device.id)}
                          />
                        }
                        disablePadding
                      >
                        <ListItemButton onClick={handleCheck(device.id)}>
                          <ListItemText
                            id={device.id}
                            primary={device.id + ' - ' + device.profile.name}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  }}
                </FixedSizeList>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="info" variant="outlined" onClick={onClose}>
            <Trans>Cancelar</Trans>
          </Button>
          <Button variant="contained" type="submit">
            <Trans>Confirmar</Trans>
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const DeviceCounter = (props: { selectedDevices: { id: string; name: string }[] }) => {
  const { selectedDevices } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Typography onMouseEnter={handlePopoverOpen} onMouseLeave={handlePopoverClose}>
        <Trans>Dispositivos seleccionados</Trans> <b>{selectedDevices.length}</b>
      </Typography>
      {selectedDevices.length > 0 && (
        <Popover
          id="mouse-over-popover"
          sx={{ pointerEvents: 'none' }}
          slotProps={{ paper: { variant: 'outlined', elevation: 0 } }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onClose={handlePopoverClose}
          disableRestoreFocus
        >
          <Box sx={{ padding: 1 }}>
            {selectedDevices.map(({ id, name }) => (
              <Typography key={id}>
                {id} - {name}
              </Typography>
            ))}
          </Box>
        </Popover>
      )}
    </>
  );
};

export { DeviceSelectDialog };
// export type { DeviceOfDeviceSelectDialog };
